import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { isEqual } from 'lodash-es';
import { Box, DraggableGrid, useDevice } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { CustomPageCard } from '~/components/CustomPageCard/CustomPageCard';
import { isCustomPageContentEdited, isCustomPageContentInvalid, MAX_CUSTOM_PAGES, reindexDisplayOrders, reorderVisibleKeepingHidden, sortedPages } from '~/models/custom-page/model';
import { useIoContext } from '~/providers/IoContext';
import { useAppConfig } from '~/store/store-context';
import { AddCustomPageDropdown } from './AddCustomPageDropdown/AddCustomPageDropdown';
import styles from './CustomPagesSection.module.scss';
export const GetCustomPagesSectionTID = (parentTestId)=>getTestIds(parentTestId);
function DraggableCustomPageCard(param) {
    let { renderCard, isDragActive, dragHandle } = param;
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: renderCard(isDragActive, dragHandle)
    });
}
const EMPTY_SET = new Set();
function getAmendmentCardProps(page, amendmentMode, onRemovePage) {
    if (amendmentMode == null) {
        return {
            lockedExisting: false,
            lockedDisplayScope: false,
            pageChange: undefined
        };
    }
    const lockedExisting = amendmentMode.existingPageIds.has(page.id);
    if (!lockedExisting) {
        return {
            lockedExisting: false,
            lockedDisplayScope: true,
            pageChange: {
                kind: 'added',
                onRevert: ()=>onRemovePage(page.id)
            }
        };
    }
    const isEdited = isCustomPageContentEdited(page, amendmentMode.originalPagesById.get(page.id));
    return {
        lockedExisting: true,
        lockedDisplayScope: true,
        pageChange: isEdited ? {
            kind: 'updated',
            onRevert: ()=>amendmentMode.onRevertEditedPage(page.id)
        } : undefined
    };
}
export const CustomPagesSection = (param)=>{
    let { testId, customPages, setCustomPages, isReadOnly, submittedPageIds, getSourceId, onUploadingChange, amendmentMode } = param;
    const testIds = useMemo(()=>GetCustomPagesSectionTID(testId), [
        testId
    ]);
    const { isMobileDevice } = useDevice();
    const { disableScrollAnimation } = useAppConfig();
    const { browser: { scrollIntoView } } = useIoContext();
    const hiddenPageIds = amendmentMode === null || amendmentMode === void 0 ? void 0 : amendmentMode.hiddenPageIds;
    const visiblePages = useMemo(()=>hiddenPageIds != null && hiddenPageIds.size > 0 ? customPages.filter((p)=>!hiddenPageIds.has(p.id)) : customPages, [
        customPages,
        hiddenPageIds
    ]);
    const sorted = useMemo(()=>sortedPages(visiblePages), [
        visiblePages
    ]);
    const isMaxReached = customPages.length >= MAX_CUSTOM_PAGES;
    const isDraggable = !isReadOnly && sorted.length >= 2;
    const [expandedPageIds, setExpandedPageIds] = useState(EMPTY_SET);
    const [_isDragging, setIsDragging] = useState(false);
    const cardRefs = useRef({});
    const handleAddPage = useCallback((page, options)=>{
        const pageToAdd = amendmentMode != null ? _object_spread_props(_object_spread({}, page), {
            displayScope: 'proposalAndAgreement'
        }) : page;
        setCustomPages((prev)=>[
                ...prev,
                pageToAdd
            ]);
        if (options.initialExpanded) {
            setExpandedPageIds((prev)=>new Set(prev).add(pageToAdd.id));
        }
        requestAnimationFrame(()=>{
            const el = cardRefs.current[pageToAdd.id];
            if (el != null) {
                scrollIntoView(el, {
                    disableScrollAnimation
                });
            }
        });
    }, [
        amendmentMode,
        setCustomPages,
        disableScrollAnimation
    ]);
    const handleRemovePage = useCallback((pageId)=>{
        setCustomPages((prev)=>reindexDisplayOrders(prev.filter((p)=>p.id !== pageId)));
        setExpandedPageIds((prev)=>{
            const next = new Set(prev);
            next.delete(pageId);
            return next;
        });
    }, [
        setCustomPages
    ]);
    const handleToggleExpanded = useCallback((pageId)=>{
        setExpandedPageIds((prev)=>{
            const next = new Set(prev);
            if (next.has(pageId)) {
                next.delete(pageId);
            } else {
                next.add(pageId);
            }
            return next;
        });
    }, []);
    const customPagesRef = useRef(customPages);
    customPagesRef.current = customPages;
    useEffect(()=>{
        if (submittedPageIds == null || submittedPageIds.size === 0) return;
        const invalidIds = customPagesRef.current.filter((p)=>submittedPageIds.has(p.id) && isCustomPageContentInvalid(p)).map((p)=>p.id);
        if (invalidIds.length === 0) return;
        setExpandedPageIds((prev)=>{
            const next = new Set(prev);
            invalidIds.forEach((id)=>next.add(id));
            return next;
        });
    }, [
        submittedPageIds
    ]);
    const handlePageUpdate = useCallback((pageId, updater)=>{
        setCustomPages((prev)=>{
            let changed = false;
            const next = prev.map((p)=>{
                if (p.id !== pageId) return p;
                const updated = updater(p);
                if (isEqual(p, updated)) return p;
                changed = true;
                return updated;
            });
            return changed ? next : prev;
        });
    }, [
        setCustomPages
    ]);
    const handleTitleChange = useCallback((pageId, newTitle)=>{
        setCustomPages((prev)=>prev.map((p)=>p.id === pageId ? _object_spread_props(_object_spread({}, p), {
                    pageTitle: newTitle
                }) : p));
    }, [
        setCustomPages
    ]);
    const handleDisplayScopeChange = useCallback((pageId, scope)=>{
        setCustomPages((prev)=>prev.map((p)=>p.id === pageId ? _object_spread_props(_object_spread({}, p), {
                    displayScope: scope
                }) : p));
    }, [
        setCustomPages
    ]);
    const handleReorder = useCallback((reorderedItems)=>{
        const reorderedIds = reorderedItems.map((item)=>item.id);
        setCustomPages((prev)=>reorderVisibleKeepingHidden(prev, reorderedIds, hiddenPageIds !== null && hiddenPageIds !== void 0 ? hiddenPageIds : EMPTY_SET));
    }, [
        setCustomPages,
        hiddenPageIds
    ]);
    const renderPageCard = useCallback((page, isDragActive, dragHandle)=>{
        var _ref;
        const { lockedExisting, lockedDisplayScope, pageChange } = getAmendmentCardProps(page, amendmentMode, handleRemovePage);
        return /*#__PURE__*/ _jsx(Box, {
            ref: (el)=>cardRefs.current[page.id] = el,
            children: /*#__PURE__*/ _jsx(CustomPageCard, {
                testId: "custom-page-card-".concat(page.id),
                page: page,
                isReadOnly: isReadOnly,
                expanded: expandedPageIds.has(page.id),
                onToggleExpanded: ()=>handleToggleExpanded(page.id),
                submitAttempted: (_ref = submittedPageIds === null || submittedPageIds === void 0 ? void 0 : submittedPageIds.has(page.id)) !== null && _ref !== void 0 ? _ref : false,
                onRemove: handleRemovePage,
                onPageUpdate: handlePageUpdate,
                onTitleChange: (newTitle)=>handleTitleChange(page.id, newTitle),
                onDisplayScopeChange: (scope)=>handleDisplayScopeChange(page.id, scope),
                isDragActive: isDragActive,
                dragHandle: dragHandle,
                getSourceId: getSourceId,
                lockedExisting: lockedExisting,
                lockedDisplayScope: lockedDisplayScope,
                pageChange: pageChange
            })
        });
    }, [
        isReadOnly,
        expandedPageIds,
        submittedPageIds,
        handleToggleExpanded,
        handleRemovePage,
        handlePageUpdate,
        handleTitleChange,
        handleDisplayScopeChange,
        getSourceId,
        amendmentMode
    ]);
    return /*#__PURE__*/ _jsxs(Box, {
        testId: testIds.CustomPagesSectionTID.Root(),
        className: cn(styles.root, {
            mobile: isMobileDevice
        }),
        children: [
            sorted.length > 0 && /*#__PURE__*/ _jsx(Box, {
                testId: testIds.CustomPagesSectionTID.CardList(),
                className: styles.cardList,
                children: isDraggable ? /*#__PURE__*/ _jsx("div", {
                    style: {
                        '--draggable-grid-item-radius': '8px'
                    },
                    children: /*#__PURE__*/ _jsx(DraggableGrid, {
                        bi: {
                            name: 'reorder_custom_page'
                        },
                        items: sorted.map((page)=>({
                                id: page.id,
                                renderCard: (isDragActive, dragHandle)=>renderPageCard(page, isDragActive, dragHandle)
                            })),
                        itemComponent: DraggableCustomPageCard,
                        onReorder: handleReorder,
                        onDragStateChange: setIsDragging,
                        dragMode: "withGhost",
                        layout: {
                            columns: 1
                        },
                        itemDragActivation: "handle",
                        itemHandlePosition: "inside",
                        gap: 0,
                        testId: "custom-pages-grid"
                    })
                }) : sorted.map((page)=>renderPageCard(page))
            }),
            !isReadOnly && /*#__PURE__*/ _jsx(AddCustomPageDropdown, {
                testId: testId,
                nextDisplayOrder: customPages.length,
                onAddPage: handleAddPage,
                isMaxReached: isMaxReached,
                getSourceId: getSourceId,
                onUploadingChange: onUploadingChange,
                amendmentMode: amendmentMode != null
            })
        ]
    });
};
